import * as React from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/utils/cn";

type StatCardProps = {
  label: string;
  value: React.ReactNode;
  unit?: string;
  suffix?: string;
  percent?: number;
  category?: string;
  indicator?: "success" | "error" | null;
  className?: string;
  loading?: boolean;
};

export function StatCard({
  label,
  value,
  unit,
  suffix,
  percent,
  category = "All",
  indicator = null,
  className,
  loading,
}: StatCardProps) {
  const clampedPercent =
    typeof percent === "number"
      ? Math.min(100, Math.max(0, percent))
      : null;
  const hasProgress = clampedPercent !== null;

  return (
    <Card className={cn("flex h-full flex-col gap-3 p-5", className)}>
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm text-muted-foreground">{label}</span>
        <Badge variant="muted" className="capitalize">
          {category}
        </Badge>
      </div>
      <div
        className={cn(
          "flex items-baseline justify-between gap-2",
          !hasProgress ? "mt-auto" : "",
        )}
      >
        <div className="flex items-baseline gap-2">
          {indicator === "success" && (
            <span
              className="inline-flex size-4 items-center justify-center rounded-full border-2 border-emerald-400"
              aria-hidden
            />
          )}
          {indicator === "error" && (
            <span
              className="inline-flex size-4 items-center justify-center rounded-full border-2 border-destructive"
              aria-hidden
            />
          )}
          {loading ? (
            <Skeleton className="h-8 w-24" />
          ) : (
            <span
              className={cn(
                "font-semibold tracking-tight text-foreground",
                hasProgress ? "text-3xl" : "text-4xl",
              )}
            >
              {value}
            </span>
          )}
          {unit && !loading && (
            <span className="text-sm text-muted-foreground">{unit}</span>
          )}
        </div>
        {suffix && !loading && (
          <span className="text-sm text-muted-foreground">{suffix}</span>
        )}
      </div>
      <div
        className={cn(
          "h-1 w-full overflow-hidden rounded-full bg-muted",
          !hasProgress || loading ? "invisible" : "",
        )}
        role={hasProgress && !loading ? "progressbar" : undefined}
        aria-valuenow={
          hasProgress && !loading ? clampedPercent : undefined
        }
        aria-valuemin={hasProgress && !loading ? 0 : undefined}
        aria-valuemax={hasProgress && !loading ? 100 : undefined}
      >
        {hasProgress && !loading && (
          <div
            className="h-full rounded-full bg-emerald-400 transition-[width] duration-500 ease-out"
            style={{ width: `${clampedPercent}%` }}
          />
        )}
      </div>
    </Card>
  );
}
